import { useState, type FormEvent } from "react";
import {
  DEFAULT_COMMITMENT_YEARS,
  DEFAULT_HORIZON_YEARS,
  MAX_COMMITMENT_YEARS,
  MAX_HORIZON_YEARS,
  MIN_COMMITMENT_YEARS,
  MIN_HORIZON_YEARS,
} from "@shared/constants";
import type { CreateProductRequest, ProductStatusName } from "@shared/schemas/product";
import { Button } from "@client/components/Button";
import { NumberField, SelectField, TextAreaField, TextField } from "@client/components/Field";
import { describeError, fieldErrors } from "@client/lib/errorMessage";

/**
 * Create and edit share one form, because they are the same eight fields and a
 * second copy would be the one that forgets the currency rule.
 *
 * Validation is deliberately thin here: the zod schemas in src/shared are the
 * contract, the server runs them, and this reflects what it says back rather
 * than keeping a second opinion that can disagree.
 */

export interface ProductFormValues {
  name: string;
  description: string;
  currencyCode: string;
  defaultHorizonYears: number;
  defaultCommitmentYears: number;
  status: ProductStatusName;
}

const emptyProductValues: ProductFormValues = {
  name: "",
  description: "",
  currencyCode: "USD",
  defaultHorizonYears: DEFAULT_HORIZON_YEARS,
  defaultCommitmentYears: DEFAULT_COMMITMENT_YEARS,
  status: "ACTIVE",
};

/** Codes this shop quotes in. Free text is still accepted — the list is a shortcut. */
const COMMON_CURRENCIES = ["USD", "MVR", "EUR", "GBP", "INR", "SGD", "AUD"];

const STATUS_OPTIONS: { value: ProductStatusName; label: string; hint: string }[] = [
  { value: "ACTIVE", label: "Active", hint: "Visible to everyone who can see the product." },
  { value: "DRAFT", label: "Draft", hint: "Still being set up." },
  { value: "ARCHIVED", label: "Archived", hint: "Read-only; nothing new can be saved into it." },
];

interface ProductFormProps {
  initial?: ProductFormValues;
  /** True once a scenario exists: the currency is frozen (docs/06-api.md#products). */
  currencyLocked?: boolean;
  submitLabel: string;
  busy?: boolean;
  error?: unknown;
  onSubmit: (values: CreateProductRequest) => void;
  onCancel?: () => void;
}

export function ProductForm({
  initial = emptyProductValues,
  currencyLocked = false,
  submitLabel,
  busy = false,
  error,
  onSubmit,
  onCancel,
}: ProductFormProps) {
  const [values, setValues] = useState<ProductFormValues>(initial);
  const serverErrors = fieldErrors(error);

  function set<K extends keyof ProductFormValues>(key: K, value: ProductFormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    onSubmit({
      name: values.name.trim(),
      description: values.description.trim() || null,
      currencyCode: values.currencyCode.trim().toUpperCase(),
      defaultHorizonYears: values.defaultHorizonYears,
      defaultCommitmentYears: values.defaultCommitmentYears,
      status: values.status,
    });
  }

  const commitment = values.defaultCommitmentYears;

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
      <TextField
        label="Name"
        required
        autoFocus
        maxLength={80}
        value={values.name}
        error={serverErrors.name}
        onChange={(event) => set("name", event.target.value)}
        hint="The product being priced — how people will find it in the list."
      />

      <TextAreaField
        label="Description"
        rows={2}
        maxLength={1000}
        value={values.description}
        error={serverErrors.description}
        onChange={(event) => set("description", event.target.value)}
        hint="Optional. One line on what it is."
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          label="Currency"
          required
          list="currency-codes"
          maxLength={3}
          disabled={currencyLocked}
          value={values.currencyCode}
          error={serverErrors.currencyCode}
          onChange={(event) => set("currencyCode", event.target.value.toUpperCase())}
          hint={
            currencyLocked
              ? "Fixed: scenarios are already priced in it."
              : "Three letters, such as USD."
          }
        />
        <datalist id="currency-codes">
          {COMMON_CURRENCIES.map((code) => (
            <option key={code} value={code} />
          ))}
        </datalist>

        <NumberField
          label="Horizon"
          required
          min={MIN_HORIZON_YEARS}
          max={MAX_HORIZON_YEARS}
          step={1}
          value={values.defaultHorizonYears}
          error={serverErrors.defaultHorizonYears}
          onChange={(event) => set("defaultHorizonYears", Number(event.target.value))}
          hint={`Years a new scenario projects (${MIN_HORIZON_YEARS}–${MAX_HORIZON_YEARS}).`}
        />

        <NumberField
          label="Commitment"
          required
          min={MIN_COMMITMENT_YEARS}
          max={MAX_COMMITMENT_YEARS}
          step={1}
          value={values.defaultCommitmentYears}
          error={serverErrors.defaultCommitmentYears}
          onChange={(event) => set("defaultCommitmentYears", Number(event.target.value))}
          // The consequence, not the number: "2" on its own tells nobody when a
          // client is first free to leave.
          hint={
            commitment <= 0
              ? "Clients can leave in any year."
              : `Clients can first leave in year ${commitment + 1}.`
          }
        />
      </div>

      <SelectField
        label="Status"
        value={values.status}
        error={serverErrors.status}
        onChange={(event) => set("status", event.target.value as ProductStatusName)}
        hint={STATUS_OPTIONS.find((option) => option.value === values.status)?.hint}
      >
        {STATUS_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </SelectField>

      {error !== undefined && error !== null && Object.keys(serverErrors).length === 0 && (
        <p role="alert" className="text-sm font-semibold" style={{ color: "var(--chart-crit)" }}>
          {describeError(error)}
        </p>
      )}

      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={busy || values.name.trim().length === 0}>
          {busy ? "Saving…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}
